/**
 * Add/Edit Package dialog (v2 — DNKADM-0102 U2).
 *
 * A MUI `Dialog` over the editable `offering` fields (DNKAPI-0181): **name** (required,
 * unique on `(tenant, name)`), **available at** (the {@link LocationAvailabilityField} —
 * the owned locations the package is sold at, empty ⇒ ALL locations; DNKAPI-0254),
 * **price** (required-in-practice — blocked on empty even though the backend is lenient),
 * **description** (optional), **who can buy** (`purchaseAudience`, DNKADM-0412 / DNKAPI-0577;
 * default everyone — sent on create, and on edit only when changed), **charge tax**
 * (`taxable`, DNKADM-0416; default off — sent on every create and every edit). **`kind`
 * is fixed to `package`** — it is NOT an editable control; `offerings.ts` injects
 * `kind:'package'` + `termDays/provider/providerProductId: null` on every write, so a
 * package can never be flipped to a membership here.
 *
 * The by-bucket **credit grants** reuse the shared {@link PlanCreditsEditor} (no fork).
 * The grant editor is:
 *  - **edit mode** — LIVE against the existing offering id (`getOfferingCredits` /
 *    `setOfferingCredit` / `deleteOfferingCredit`): each row Add/Remove hits the network
 *    immediately, independent of this dialog's field-Save.
 *  - **create mode** — STAGED locally (no id yet): rows are collected in a ref, then on
 *    Save the package is **created FIRST** (`POST /offerings` → its `{id}`) and the staged
 *    grants are attached AFTER via `PUT …/credits/{bucketId}` (you can't grant to a package
 *    that doesn't exist yet — order matters).
 *
 * A package with **zero grants** is sellable but grants nothing — a NON-BLOCKING warning
 * shows (it still saves). A validation `400` / a `409` (duplicate name) renders INLINE
 * (`classifyFormError`); anything else raises an error toast. Mirrors `PlanDialog`.
 */
import { useCallback, useId, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  createPackage,
  deleteOfferingCredit,
  getOfferingCredits,
  setOfferingCredit,
  updatePackage,
  type Offering,
  type OfferingInput,
  type PurchaseAudience,
} from '@/api/offerings';
import type { ShowToast } from '@/components/toastContext';
import {
  PlanCreditsEditor,
  type CreditGrant,
  type CreditGrantsEditorCopy,
} from './PlanCreditsEditor';
import { LocationAvailabilityField } from './LocationAvailabilityField';
import { classifyFormError } from '@/api/screenError';

export interface PackageDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The package being edited, or `null` to add a new one. */
  pkg: Offering | null;
  /** The tenant currency code (for the price adornment), or null while it loads. */
  currency: string | null;
  /** Close the dialog without saving. */
  onClose: () => void;
  /** Called after a successful create/update so the caller can refetch the list. */
  onSaved: () => void;
  /** Raise a non-blocking toast (upstream-error). */
  showToast: ShowToast;
}

interface FieldErrors {
  name?: string;
  price?: string;
}

/** Grant-centric copy for the shared credit editor (Plans keeps the default wording). */
const PACKAGE_CREDIT_COPY: CreditGrantsEditorCopy = {
  heading: 'Credit grants',
  hint: 'The credits this package grants once when it’s purchased.',
  loadError: 'Couldn’t load the credit grants.',
  tableAriaLabel: 'Package credit grants',
  removeAriaLabel: (name) => `Remove credit grant for ${name}`,
  addButton: 'Add grant',
  savedToast: 'Credit grant saved',
  removedToast: 'Credit grant removed',
  saveError: 'Couldn’t save the credit grant.',
  removeError: 'Couldn’t remove the credit grant.',
  chooseBucketError: 'Choose a credit bucket.',
  amountError: 'Enter an amount (0 or more).',
  emptyBucketsInfo: 'Create a credit bucket first to set this package’s credit grants.',
  allUsedHint: 'Every credit bucket already has a grant on this package.',
};

/** What each "Who can buy" choice means in the member app (DNKADM-0412). */
const AUDIENCE_HINT: Record<PurchaseAudience, string> = {
  everyone: 'Anyone can buy it in the member app.',
  members: 'Only active members can buy it in the member app. Others see it labeled Members only.',
  staff: 'Hidden from the member app. Sell it from a walk-in sale.',
};

export function PackageDialog(props: PackageDialogProps) {
  // Remount the inner form per open / per edited package so its inputs reseed cleanly.
  if (!props.open) return null;
  return <PackageDialogForm key={props.pkg?.id ?? 'new'} {...props} />;
}

function PackageDialogForm({
  pkg,
  currency,
  onClose,
  onSaved,
  showToast,
}: Omit<PackageDialogProps, 'open'>) {
  const ids = useId();
  const isEdit = pkg !== null;

  const [name, setName] = useState(pkg?.name ?? '');
  const [locationIds, setLocationIds] = useState<string[]>(pkg?.locationIds ?? []);
  // A genuinely `$0` package must still seed the field with `0`, not an empty box —
  // only an UNPRICED package (`price: null`) opens blank (DNKADM-0245).
  const [price, setPrice] = useState(pkg?.price != null ? String(pkg.price) : '');
  const [description, setDescription] = useState(pkg?.description ?? '');
  const [purchaseAudience, setPurchaseAudience] = useState<PurchaseAudience>(
    pkg?.purchaseAudience ?? 'everyone',
  );
  const [taxable, setTaxable] = useState(pkg?.taxable ?? false);

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Grant count drives the zero-grant warning; the editor reports it on each (re)load.
  const [grantCount, setGrantCount] = useState(isEdit ? (pkg?.credits.length ?? 0) : 0);

  // Create mode has no id yet — grants (amount + DNKADM-0127 expiryDays) are staged
  // here and flushed after the POST.
  const stagedRef = useRef<Map<string, { amount: number; expiryDays: number }>>(new Map());
  const loadStaged = useCallback(
    async (_id: string): Promise<CreditGrant[]> =>
      Array.from(stagedRef.current, ([creditBucketId, { amount, expiryDays }]) => ({
        creditBucketId,
        amount,
        expiryDays,
      })),
    [],
  );
  const setStaged = useCallback(
    async (_id: string, bucketId: string, amount: number, expiryDays = 0) => {
      stagedRef.current.set(bucketId, { amount, expiryDays });
    },
    [],
  );
  const deleteStaged = useCallback(async (_id: string, bucketId: string) => {
    stagedRef.current.delete(bucketId);
  }, []);

  function validate(): OfferingInput | null {
    const errors: FieldErrors = {};

    const tName = name.trim();
    if (tName === '') errors.name = 'Enter a name.';

    // Price is required-in-practice — block on empty even if the backend is lenient.
    const priceValue = Number(price);
    if (price.trim() === '' || !Number.isFinite(priceValue) || priceValue < 0) {
      errors.price = 'Enter a price (0 or more).';
    }

    if (errors.name || errors.price) {
      setFieldErrors(errors);
      return null;
    }
    setFieldErrors({});

    const input: OfferingInput = {
      name: tName,
      price: priceValue,
      // Always sent — `[]` ⇒ available at all locations, else the selected set.
      locationIds,
    };
    const d = description.trim();
    if (d !== '') input.description = d;
    // Who can buy: always sent on create; on edit only when changed (absent ⇒ unchanged).
    if (pkg === null || purchaseAudience !== pkg.purchaseAudience) {
      input.purchaseAudience = purchaseAudience;
    }
    // Charge tax (DNKADM-0416): always sent, on create and on every edit (Peyton,
    // 2026-09-29). The switch starts at the stored value, so an untouched edit re-sends it
    // unchanged: idempotent, and it never lets an older API read an absent `taxable` as false.
    input.taxable = taxable;
    return input;
  }

  async function handleSave() {
    setServerError(null);
    const input = validate();
    if (!input) return;
    setSaving(true);
    try {
      if (isEdit && pkg) {
        // Fields via PUT; grants are managed live by the editor (independent sub-resource).
        await updatePackage(pkg.id, input);
      } else {
        // Create the package FIRST, THEN attach the staged grants against its id.
        const created = await createPackage(input);
        const newId = created?.id ?? null;
        if (newId !== null) {
          for (const [bucketId, { amount, expiryDays }] of stagedRef.current) {
            await setOfferingCredit(newId, bucketId, amount, expiryDays);
          }
        }
      }
      // DNKADM-0320: confirm the save (after every write resolves; one toast per action).
      showToast(isEdit ? 'Package saved' : 'Package added', 'success');
      onSaved();
    } catch (err) {
      const view = classifyFormError(err, 'That package isn’t valid — check the name and price.', {
        toastFallback: 'The package couldn’t be saved. Please try again.',
      });
      if (view.kind === 'inline') setServerError(view.message);
      else showToast(view.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  const titleId = `${ids}-title`;
  const priceId = `${ids}-price`;

  const editorProps =
    isEdit && pkg
      ? {
          entityId: pkg.id,
          loadCredits: getOfferingCredits,
          setCredit: setOfferingCredit,
          deleteCredit: deleteOfferingCredit,
          // DNKADM-0127: offerings expose the per-credit expiry control.
          showExpiry: true,
        }
      : {
          entityId: '',
          loadCredits: loadStaged,
          setCredit: setStaged,
          deleteCredit: deleteStaged,
          showExpiry: true,
        };

  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>{isEdit ? 'Edit package' : 'Add package'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 0.5 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            size="small"
            required
            fullWidth
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name ?? 'What the buyer sees in the catalog.'}
            slotProps={{ htmlInput: { 'aria-label': 'Name' } }}
          />

          {/* The owned locations this package is available at (empty ⇒ all locations). */}
          <LocationAvailabilityField
            value={locationIds}
            onChange={setLocationIds}
            disabled={saving}
          />

          <TextField
            id={priceId}
            label="Price"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            disabled={saving}
            size="small"
            fullWidth
            required
            error={Boolean(fieldErrors.price)}
            helperText={fieldErrors.price}
            slotProps={{
              ...(currency
                ? { input: { startAdornment: <InputAdornment position="start">{currency}</InputAdornment> } }
                : {}),
              htmlInput: { inputMode: 'decimal', 'aria-label': 'Price' },
            }}
          />

          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={saving}
            size="small"
            fullWidth
            multiline
            minRows={2}
            slotProps={{ htmlInput: { 'aria-label': 'Description' } }}
          />

          {/* Who can buy (DNKADM-0412 / DNKAPI-0577) — the member app only; the front desk
              can sell and grant every package regardless. Mirrors PlanDialog's control. */}
          <Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 500, mb: 0.75 }}>Who can buy</Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={purchaseAudience}
              onChange={(_e, value: PurchaseAudience | null) => {
                if (value !== null) setPurchaseAudience(value);
              }}
              aria-label="Who can buy"
              disabled={saving}
            >
              <ToggleButton value="everyone" aria-label="Everyone">
                Everyone
              </ToggleButton>
              <ToggleButton value="members" aria-label="Members only">
                Members only
              </ToggleButton>
              <ToggleButton value="staff" aria-label="Front desk only">
                Front desk only
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
              {AUDIENCE_HINT[purchaseAudience]}
            </Typography>
          </Box>

          {/* Charge tax (DNKADM-0416 / DNKAPI-0585) — placed with Who can buy, before the
              credit grants (PlanDialog has no tax control to match). */}
          <Box>
            <FormControlLabel
              control={
                <Switch
                  checked={taxable}
                  onChange={(e) => setTaxable(e.target.checked)}
                  disabled={saving}
                  slotProps={{ input: { 'aria-label': 'Charge tax' } }}
                />
              }
              label="Charge tax"
            />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {taxable
                ? 'Tax is added when it’s sold, at the member’s home location’s tax rates.'
                : 'No tax is added when this package is sold.'}
            </Typography>
          </Box>

          <PlanCreditsEditor
            {...editorProps}
            showToast={showToast}
            copy={PACKAGE_CREDIT_COPY}
            onGrantsChange={(grants) => setGrantCount(grants.length)}
          />

          {grantCount === 0 && (
            <Alert severity="warning" role="alert">
              This package grants no credits — it will grant nothing when purchased.
            </Alert>
          )}

          {serverError && (
            <Alert severity="error" role="alert">
              {serverError}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save package' : 'Add package'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
